import { trackSubscriptionPurchase, wasTrialStartTracked } from '@shared/services/conversionTracking';
import type { ActiveEntitlement } from '../services/purchasesService';

const PURCHASE_ATTEMPT_KEY_PREFIX = 'enough_purchase_attempt:';

/** Record, just before the store sheet opens, that `uid` started a purchase
 *  on this device. The stranded resume below fires only with this mark, so an
 *  entitlement that arrived another way — a pre-login Restore, a purchase on
 *  another device — is never counted as a new trial. */
export function markPurchaseAttempt(uid: string) {
  try {
    localStorage.setItem(`${PURCHASE_ATTEMPT_KEY_PREFIX}${uid}`, '1');
  } catch {
    // storage unavailable — the resume simply won't fire
  }
}

function hasPurchaseAttempt(uid: string): boolean {
  try {
    return localStorage.getItem(`${PURCHASE_ATTEMPT_KEY_PREFIX}${uid}`) === '1';
  } catch {
    return false;
  }
}

/**
 * Fire the trial-start conversion for a stranded purchase (audit 2026-10-09).
 *
 * When the app is killed while the store sheet is up, the point of sale never
 * runs, so `conversion_trial_started` never fires — even though the store is
 * now billing an anonymous user. AuthGuard finds that user on the next launch;
 * this sends what the point of sale missed (`subscription_started` too, so
 * internal funnels see the recovery).
 *
 * - Only after a purchase attempt on this device (markPurchaseAttempt): an
 *   active trial alone could be a Restore, which is not a new trial.
 * - Store-billed entitlements only: a promotional grant is a gift, not a sale.
 * - Trials only (a missing periodType counts as a trial — the pre-login funnel
 *   only sells trials). A paid-from-day-one start is left to the server-side
 *   paid feed, which knows the real price; firing it here without a value
 *   would teach Ads a $0 purchase.
 * - Once per uid per device: the point of sale marks the uid when it fires, so
 *   a sale that got through, or a second relaunch, doesn't fire again. The
 *   shared `trial:<uid>` transaction_id dedupes in Ads as a second guard.
 *
 * Returns whether it fired.
 */
export async function trackStrandedTrialConversion(
  anonUid: string,
  entitlements: ActiveEntitlement[],
): Promise<boolean> {
  const trial = entitlements.find(
    (e) => e.store !== 'PROMOTIONAL' && (e.periodType ?? 'TRIAL') === 'TRIAL',
  );
  if (!trial || !hasPurchaseAttempt(anonUid) || wasTrialStartTracked(anonUid)) return false;
  await trackSubscriptionPurchase({ tier: trial.id, isTrial: true, uid: anonUid });
  return true;
}
